//© 2025 Kaitlyn Troxell
import React, { useState, useEffect } from "react";
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

function EventDetailsModal({ 
  selectedCalendarEvent, 
  setSelectedCalendarEvent, 
  updateCalendarEventNotes,
  updateCalendarEventEndDate,
  deleteCalendarEventById,
  updateTaskCompletion,
  updateCalendarEvent,
  onChangeEndDate,
  isAdmin = true
}) {
  const [isCompleted, setIsCompleted] = useState(
    selectedCalendarEvent.task?.completed || false
  );
  
  // Initialize subtasks state from task data
  const [subtasksState, setSubtasksState] = useState(
    selectedCalendarEvent.task?.subtasks || []
  );

  // Check if user is on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update subtasks when selectedCalendarEvent changes
  useEffect(() => {
    setSubtasksState(selectedCalendarEvent.task?.subtasks || []);
    setIsCompleted(selectedCalendarEvent.task?.completed || false);
  }, [selectedCalendarEvent.task?.id, selectedCalendarEvent.task?.subtasks, selectedCalendarEvent.task?.completed]);

  const handleChangeEndDate = () => {
    if (onChangeEndDate) {
      onChangeEndDate();
    }
  };

  const handleNotesChange = (e) => {
    setSelectedCalendarEvent(prev => ({
      ...prev,
      notes: e.target.value
    }));
  };

  const handleCompletedChange = async (e) => {
    const completed = e.target.checked;
    setIsCompleted(completed);

    if (selectedCalendarEvent.task?.id) {
      try {
        await updateTaskCompletion(selectedCalendarEvent.task.id, completed);
      } catch (error) {
        setIsCompleted(!completed);
        alert("Failed to update task completion status");
      }
    }
  };

  // Handle individual subtask checkbox change
  const handleSubtaskChange = async (index) => {
    try {
      const updatedSubtasks = [...subtasksState];
      updatedSubtasks[index] = {
        ...updatedSubtasks[index],
        completed: !updatedSubtasks[index].completed
      };
      
      setSubtasksState(updatedSubtasks);

      // Check if all subtasks are now completed
      const allSubtasksCompleted = updatedSubtasks.every(subtask => subtask.completed);

      // Update in Firestore
      if (selectedCalendarEvent.task?.id) {
        const taskRef = doc(db, 'tasks', selectedCalendarEvent.task.id);
        const updateData = {
          subtasks: updatedSubtasks
        };

        // If all subtasks are completed, mark parent task as completed
        if (allSubtasksCompleted && !isCompleted) {
          updateData.completed = true;
          updateData.date_completed = new Date();
          setIsCompleted(true);
          
          // Also update via the updateTaskCompletion function if available
          if (updateTaskCompletion) {
            await updateTaskCompletion(selectedCalendarEvent.task.id, true);
          }
        }
        // If any subtask is uncompleted and parent was completed, unmark parent
        else if (!allSubtasksCompleted && isCompleted) {
          updateData.completed = false;
          updateData.date_completed = null;
          setIsCompleted(false);
          
          if (updateTaskCompletion) {
            await updateTaskCompletion(selectedCalendarEvent.task.id, false);
          }
        }

        await updateDoc(taskRef, updateData);
      }
    } catch (error) {
      console.error("Error updating subtask:", error);
      alert("Failed to update subtask");
    }
  };
  
  // Handle start date change (mobile only)
  const handleStartDateChange = async (e) => {
    const newStartDateStr = e.target.value;
    const newStartDate = new Date(newStartDateStr);
    newStartDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
    
    // Calculate new end date (maintain duration)
    const currentStart = selectedCalendarEvent.start;
    const currentEnd = selectedCalendarEvent.end || new Date(currentStart.getTime() + 86400000); // +1 day default
    const duration = currentEnd.getTime() - currentStart.getTime();
    const newEndDate = new Date(newStartDate.getTime() + duration);
    
    // Update local state
    setSelectedCalendarEvent(prev => ({
      ...prev,
      start: newStartDate,
      end: newEndDate
    }));

    // Update in Firestore
    if (updateCalendarEvent) {
      try {
        await updateCalendarEvent(selectedCalendarEvent.id, {
          start: newStartDate,
          end: newEndDate
        });
      } catch (error) {
        console.error("Error updating start date:", error);
        alert("Failed to update start date");
      }
    }
  };

  // Handle end date change
  const handleEndDateChange = async (e) => {
    const newEndDateStr = e.target.value;
    
    // Convert string to Date object and add 1 day (since FullCalendar end dates are exclusive)
    const newEndDate = new Date(newEndDateStr);
    newEndDate.setDate(newEndDate.getDate() + 1);
    newEndDate.setHours(12, 0, 0, 0);
    
    // Update local state immediately for UI feedback
    setSelectedCalendarEvent(prev => ({
      ...prev,
      end: newEndDate
    }));

    // Save to Firestore and update calendar immediately
    if (updateCalendarEventEndDate) {
      try {
        await updateCalendarEventEndDate(selectedCalendarEvent.id, newEndDate);
      } catch (error) {
        console.error("Error updating end date:", error);
        alert("Failed to update end date");
      }
    }
  };

  const handleSaveNotes = async () => {
    await updateCalendarEventNotes(selectedCalendarEvent.id, selectedCalendarEvent.notes || '');
    setSelectedCalendarEvent(null);
  };

  const handleDeleteEvent = async () => {
    if (window.confirm("Are you sure you want to delete this task from the calendar?")) {
      await deleteCalendarEventById(selectedCalendarEvent.id);
      setSelectedCalendarEvent(null);
    }
  };

  // Helper to format date for input[type="date"]
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Check if task has subtasks
  const hasSubtasks = subtasksState && subtasksState.length > 0;

  return (
    <div className="modal-backdrop">
      <div className="modal-container event-modal">
        <h3 className="modal-title">Task Details</h3>
        
        <div className="event-detail">
          <strong>Task:</strong> {selectedCalendarEvent.task?.title}
        </div>
        
        <div className="event-detail">
          <strong>Phase:</strong> {selectedCalendarEvent.task?.phase}
        </div>
        
        {/* Start Date - Editable on Mobile */}
        <div className="event-detail">
          <strong>Start Date:</strong>
          {isMobile && isAdmin ? (
            <input
              type="date"
              value={formatDateForInput(selectedCalendarEvent.start)}
              onChange={handleStartDateChange}
              className="form-input"
              style={{ marginTop: '5px' }}
            />
          ) : (
            <span style={{ marginLeft: '8px' }}>
              {selectedCalendarEvent.start.toLocaleDateString()}
            </span>
          )}
        </div>

        {/* End Date - Editable on Mobile */}
        <div className="event-detail">
          <strong>End Date:</strong>
          {isMobile && isAdmin ? (
            <input
              type="date"
              value={
                selectedCalendarEvent.end
                  ? (() => {
                      const endDate = selectedCalendarEvent.end instanceof Date
                        ? new Date(selectedCalendarEvent.end)
                        : new Date(selectedCalendarEvent.end);
                      endDate.setDate(endDate.getDate() - 1);
                      return formatDateForInput(endDate);
                    })()
                  : formatDateForInput(selectedCalendarEvent.start)
              }
              onChange={handleEndDateChange}
              className="form-input"
              style={{ marginTop: '5px' }}
            />
          ) : (
            <span style={{ marginLeft: '8px' }}>
              {selectedCalendarEvent.end
                ? (() => {
                    const endDate = selectedCalendarEvent.end instanceof Date
                      ? new Date(selectedCalendarEvent.end)
                      : new Date(selectedCalendarEvent.end);
                    endDate.setDate(endDate.getDate() - 1);
                    return endDate.toLocaleDateString();
                  })()
                : 'Not set'}
            </span>
          )}
        </div>

        {isMobile && isAdmin && (
          <div style={{ 
            fontSize: '12px', 
            color: '#666', 
            fontStyle: 'italic',
            marginTop: '5px',
            padding: '8px',
            backgroundColor: '#f0f7ff',
            borderRadius: '4px',
            border: '1px solid #d0e7ff'
          }}>
            💡 Tip: You can change the start and end dates above
          </div>
        )}

        {/* Subtasks Section */}
        {hasSubtasks && (
          <div className="event-detail">
            <strong>Subtasks:</strong>

            {/* Subtask Checkboxes */}
            <div style={{ 
              marginTop: '8px',
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '8px',
              backgroundColor: '#f8f9fa'
            }}>
              {subtasksState.map((subtask, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 4px',
                    borderBottom: index < subtasksState.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={subtask.completed || false}
                    onChange={() => handleSubtaskChange(index)}
                    style={{
                      width: '18px',
                      height: '18px',
                      marginRight: '10px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{
                    fontSize: '14px',
                    color: subtask.completed ? '#666' : '#333',
                    textDecoration: subtask.completed ? 'line-through' : 'none',
                    flex: 1
                  }}>
                    {subtask.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Main Completed Checkbox - only if no subtasks */}
        {!hasSubtasks && (
          <div className="event-detail">
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={isCompleted}
                  onChange={handleCompletedChange}
                  className="form-checkbox"
                />
                <span>Mark as Completed</span>
              </label>
            </div>
            {isCompleted && selectedCalendarEvent.task?.date_completed && (
              <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                Completed on: {
                  selectedCalendarEvent.task.date_completed.toDate 
                    ? selectedCalendarEvent.task.date_completed.toDate().toLocaleDateString()
                    : new Date(selectedCalendarEvent.task.date_completed).toLocaleDateString()
                }
              </div>
            )}
          </div>
        )}
        
        {selectedCalendarEvent.contractor && (
          <div className="event-detail">
            <strong>Assigned Contractor:</strong>
            <div className="contractor-info">
              <div className="contractor-info-name">
                {selectedCalendarEvent.contractor.name}
              </div>
              <div className="contractor-info-details">
                {selectedCalendarEvent.contractor.trade} | {selectedCalendarEvent.contractor.phone}
              </div>
              {selectedCalendarEvent.contractor.email && (
                <div className="contractor-info-details">
                  {selectedCalendarEvent.contractor.email}
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="event-detail">
          <strong>Notes:</strong>
          <textarea
            value={selectedCalendarEvent.notes || ''}
            onChange={handleNotesChange}
            className="notes-textarea"
            placeholder="Add notes about this task..."
          />
        </div>

        <div className="modal-actions event-actions">
          <button
            onClick={handleDeleteEvent}
            className="delete-btn"
          >
            Delete Task
          </button>
          
          <div className="action-group">
            <button
              onClick={handleSaveNotes}
              className="save-btn"
            >
              Save Notes
            </button>
            
            <button
              onClick={() => setSelectedCalendarEvent(null)}
              className="modal-cancel-btn"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetailsModal;